import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  SimpleChanges
} from '@angular/core';
import {take} from 'rxjs';
import {Company} from '../../../../../_models/company';
import {EffectiveRole, User} from '../../../../../_models/user';
import {CompanyWorkspaceService} from '../../../../../_services/company-workspace.service';
import {UsersService} from '../../../../../_services/users.service';
import {GlobalStore} from '../../../../../global.store';

@Component({
  selector: 'app-company-switcher',
  templateUrl: './company-switcher.component.html',
  styleUrls: ['./company-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompanySwitcherComponent {
  @Input() currentCompany: Company | null = null;
  @Input() currentUser: User | null = null;
  @Input() variant: 'sidebar' | 'mobile' = 'sidebar';

  isOpen = false;
  isLoading = false;
  isSwitching = false;
  companies: Company[] = [];
  availableCompanies: Company[] = [];
  private availableCompaniesLoadedForUserId: number | null = null;
  private isLoadingAvailableCompanies = false;

  readonly roleLabels: Record<string, string> = {
    [EffectiveRole.ROLE_COMPANY_ADMIN]: 'Admin',
    [EffectiveRole.ROLE_USER]: 'User',
    [EffectiveRole.ROLE_TRADE]: 'Trade'
  };

  constructor(
    private usersService: UsersService,
    private companyWorkspaceService: CompanyWorkspaceService,
    private globalStore: GlobalStore,
    private cdRef: ChangeDetectorRef
  ) {
  }

  get canShow(): boolean {
    return !!this.currentCompany && !!this.currentUser && this.currentUser.effectiveRole !== EffectiveRole.ROLE_SUPER_ADMIN;
  }

  get canSwitch(): boolean {
    return this.availableCompanies.length > 1;
  }

  get isScrollableList(): boolean {
    return this.companies.length >= 12;
  }

  get currentCompanyName(): string {
    return this.currentCompany?.name ?? '';
  }

  get loaderSkeletonRows(): number[] {
    const rowsCount = Math.min(Math.max(this.availableCompanies.length || 6, 4), 12);
    return Array.from({length: rowsCount}, (_, index) => index);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['currentCompany'] || changes['currentUser']) && this.canShow) {
      this.loadAvailableCompanies();
    }
  }

  toggle(): void {
    if (!this.canShow || !this.canSwitch || this.isSwitching) {
      return;
    }

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  open(): void {
    const fallbackCompanies = this.availableCompanies.length
      ? this.availableCompanies
      : (this.currentUser?.companies ?? []);

    this.isOpen = true;
    this.isLoading = fallbackCompanies.length === 0;
    this.companies = [...fallbackCompanies];
    this.cdRef.markForCheck();

    this.usersService.getCurrentUserCompanies().pipe(take(1)).subscribe({
      next: (companies) => {
        this.companies = [...companies];
        this.availableCompanies = [...companies];
        this.availableCompaniesLoadedForUserId = this.currentUser?.id ?? null;
        this.isLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.companies = [...(this.currentUser?.companies ?? [])];
        this.availableCompanies = [...this.companies];
        this.isLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  close(): void {
    this.isOpen = false;
    this.isLoading = false;
    this.cdRef.markForCheck();
  }

  isActiveCompany(company: Company): boolean {
    return !!company?.id && company.id === this.currentCompany?.id;
  }

  getRoleLabel(company: Company): string {
    return this.roleLabels[company.companyRole] ?? '-';
  }

  getRoleClass(company: Company): string {
    switch (company.companyRole) {
      case EffectiveRole.ROLE_COMPANY_ADMIN:
        return 'company-switcher__role--admin';
      case EffectiveRole.ROLE_TRADE:
        return 'company-switcher__role--trade';
      default:
        return 'company-switcher__role--user';
    }
  }

  switchToCompany(company: Company): void {
    if (!company?.id || this.isActiveCompany(company) || this.isSwitching) {
      return;
    }

    this.isSwitching = true;
    this.companyWorkspaceService.rememberLastCompany(company.id);

    const destination = this.companyWorkspaceService.getCompanySwitcherDestination(company);
    window.location.assign(destination.url);
  }

  openAllCompanies(): void {
    if (this.isSwitching) {
      return;
    }

    this.isSwitching = true;
    this.companyWorkspaceService.clearCurrentCompanyContext();
    this.globalStore.resetState();
    const destination = this.companyWorkspaceService.getAllCompaniesDestination(true);
    window.location.assign(destination.url);
  }

  trackByCompanyId(_: number, company: Company): number {
    return company.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private loadAvailableCompanies(): void {
    const userId = this.currentUser?.id ?? null;

    if (!userId || this.isLoadingAvailableCompanies || this.availableCompaniesLoadedForUserId === userId) {
      return;
    }

    this.isLoadingAvailableCompanies = true;
    this.usersService.getCurrentUserCompanies().pipe(take(1)).subscribe({
      next: (companies) => {
        this.availableCompanies = [...companies];
        this.availableCompaniesLoadedForUserId = userId;
        this.isLoadingAvailableCompanies = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.availableCompanies = [...(this.currentUser?.companies ?? [])];
        this.isLoadingAvailableCompanies = false;
        this.cdRef.markForCheck();
      }
    });
  }
}
